import { CreateLabelForm } from '@aibees/elephant/components/create-label/components/create-label-form';
import { CreateListForm } from '@aibees/elephant/components/create-list-form';
import { ListVisibilitySwitch } from '@aibees/elephant/components/list-visibility-switch';
import { RecordLabelManager } from '@aibees/elephant/components/record-label-manager';
import { RecordManagementPopoverHeader } from '@aibees/elephant/components/record-management-popover-header';
import { useLabelManagementContext } from '@aibees/elephant/components/search-table-action-dialog-lists/context';
import { useSearchRouteInfo, useTrackEvents } from '@aibees/elephant/hooks';
import { usePageType } from '@aibees/elephant/hooks/use-page-type';
import { useSaveRecord } from '@aibees/elephant/hooks/use-save-record';
import { ROUTES as R, ROUTES } from '@aibees/elephant/router';
import { SearchType } from '@aibees/elephant/types';
import {
  Label,
  LabelType,
  LabelVisibility,
} from '@aibees/elephant/types/label';
import { notificationIconMap } from '@aibees/elephant/utils';
import { Grid2, Icon } from '@aibees/shared/components';
import { useBoolean, useMatchRoute, useNotify } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { useWorkspace } from '@aibees/shared/workspace';
import { Button, Stack, Typography } from '@mui/material';
import { uniqBy } from 'lodash-es';
import { memo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as Styled from './styles';
import * as SX from './sx';
import type { LabelManagementPopoverProps as Props } from './types';

const ANCHOR_ORIGIN = {
  vertical: 'bottom' as const,
  horizontal: 'right' as const,
};
const TRANSFORM_ORIGIN = {
  vertical: 'top' as const,
  horizontal: 'right' as const,
};

export const LabelManagementPopover = memo(
  ({
    className,
    state,
    table,
    title,
    recordLabel,
    id,
    labelType,
    callSaveApiOnSubmit,
    parent,
    onAdd,
    currentLists,
    icon,
    onLoadingChange,
    ...other
  }: Props) => {
    const notify = useNotify();
    const applyState = useBoolean(false);
    const { track } = useTrackEvents();
    const user = useWorkspace()?.user;
    const { type, table: searchTable } = useSearchRouteInfo();
    const { isPeoplePage } = usePageType();
    const isTable = !!searchTable;
    const isPeopleLookalike = useMatchRoute(ROUTES.peopleLookalike);
    const isCompanyLookalike = useMatchRoute(ROUTES.companyLookalike);
    const isLookalike = isPeopleLookalike || isCompanyLookalike;

    const isLabel = labelType === LabelType.LABEL;

    const notificationIcon =
      labelType === LabelType.LABEL
        ? notificationIconMap.Label
        : notificationIconMap.List;
    const route = isLabel ? R.labelPage : R.listPage;
    const { onSelect, setJustCreatedLabels, labels, justCreatedLabels } =
      useLabelManagementContext();
    const showCreateSection = useBoolean(false);
    const [visibility, setVisibility] = useState<LabelVisibility>(
      LabelVisibility.INTERNAL
    );
    const [loading, setLoading] = useState(false);
    const api = useSaveRecord(
      type === SearchType.People || isPeoplePage
        ? SearchType.People
        : SearchType.Company
    );

    useEffect(() => {
      onLoadingChange?.(loading);
    }, [loading, onLoadingChange]);

    const handleChangeVisibility = (e: LabelVisibility) => {
      setVisibility(e);
    };
    const handleSubmit = (selected: Label[], unselected: Label[]) => {
      const uniqueSelected = uniqBy(selected, 'id');
      setLoading(true);
      onAdd?.(uniqueSelected, unselected);
      setJustCreatedLabels([]);
      onSelect(uniqueSelected);
      const key = Date.now().toString();
      if (callSaveApiOnSubmit && id) {
        api
          .mutateAsync({
            ids: [id],
            kind: 'ADD',
            labels: uniqueSelected.map((x) => x.id),
            user,
          })
          .then((data) => {
            notify.complete(
              `${isLabel ? 'Label' : 'List'} added!.`,
              `${isLabel ? 'Label' : 'List'} was Added.`,
              key,
              {
                icon: notificationIcon,
              }
            );
            track(isLabel ? 'ADD_LABEL_TO_RECORDS' : 'ADD_LIST_TO_RECORDS', {
              type,
              multiple: false,
              component: parent,
            });
            state.onClose();
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        onSelect(selected);
        state.onClose();
        setLoading(false);
      }
    };

    const handleCreateSuccess = (label?: Label) => {
      if (label) {
        setJustCreatedLabels([...justCreatedLabels, ...[label]]);
        onSelect([...(labels ?? []), label]);
      }
      if (isLookalike) showCreateSection.onFalse();
    };

    const handleClose = () => {
      setJustCreatedLabels([]);
      state.onClose();
      setTimeout(() => {
        showCreateSection.onFalse();
      }, 500);
    };

    const handleShowCreateSection = () => {
      showCreateSection.onTrue();
    };

    return (
      <Styled.Wrapper
        className={classnames('LabelManagementPopover', className)}
        sx={SX.root}
        contentSx={SX.content(loading)}
        state={state}
        anchorOrigin={ANCHOR_ORIGIN}
        transformOrigin={TRANSFORM_ORIGIN}
        onClose={handleClose}
        {...other}
      >
        {!isLookalike ? (
          <Stack sx={SX.titleWrapper(loading)}>
            <Stack sx={SX.row}>
              {icon && <Icon name={icon} size={20} />}

              {title}
            </Stack>

            {!isTable &&
              (!showCreateSection.value ? (
                <Button color="primary" onClick={handleShowCreateSection}>
                  <Typography sx={SX.action}>Create New</Typography>
                </Button>
              ) : (
                <Link to={route} target="_blank">
                  <Button
                    color="primary"
                    endIcon={
                      <Icon name="setting-1" size={14} color="primary" />
                    }
                  >
                    <Typography sx={SX.action}>Manage</Typography>
                  </Button>
                </Link>
              ))}
          </Stack>
        ) : (
          <RecordManagementPopoverHeader
            showCreateSection={showCreateSection.value}
            route={route}
            onCreateNew={handleShowCreateSection}
            title={isLabel ? 'Labels' : 'Lists'}
          />
        )}

        {(showCreateSection.value || isTable) &&
          (isLabel ? (
            <Grid2 sx={SX.grid}>
              <CreateLabelForm
                onSuccess={handleCreateSuccess}
                applyState={applyState}
              />
            </Grid2>
          ) : (
            <Grid2 sx={SX.header}>
              <Grid2 sx={SX.headerRow}>
                <Typography sx={SX.title}>Create New List</Typography>
                <ListVisibilitySwitch onSubmit={handleChangeVisibility} />
              </Grid2>

              <CreateListForm
                visibility={visibility}
                onSuccess={handleCreateSuccess}
                applyState={applyState}
              />
            </Grid2>
          ))}

        <RecordLabelManager
          state={state}
          title={!isLookalike ? recordLabel : ''}
          type={labelType}
          onSubmit={handleSubmit}
          onClose={handleClose}
          currentLabels={currentLists}
          loading={loading || applyState.value}
        />
      </Styled.Wrapper>
    );
  }
);

LabelManagementPopover.displayName = 'LabelManagementPopover';
